import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {Input  } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slot } from "@/components/SlotCard";
import { toast } from "sonner";
import { User, Mail, Phone, Percent, Send, IndianRupee, InfoIcon, Link2, Wallet } from "lucide-react";
import { cn, getSlotTimeRange, isValidMobile } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks'
import { getCustomerByPhone } from "@/store/thunkMidddleware/usersMiddleware";
import { ButtonWithLoader } from "./ui/buttonwithloader";
import { v4 as uuidv4 } from 'uuid';
import {
  VENDOR_CANCELLATION_SHARE_PERCENT,
  VenuePaymentSettings,
  cancellationShare,
  splitBookingPayment,
} from "@/lib/bookingPaymentSplit";

type BookingMode = "DIRECT" | "PAYMENT_LINK";

interface CreateBookingModalProps {
  open: boolean;
  onClose: () => void;
  selectedSlots?: Slot[];
  selectedDate:any;
  /** Sends a Razorpay payment link to the customer. Booking stays pending. */
  handleCreateBooking:(data:any,cd:any)=>void
  /** Books immediately; the customer pays the venue's own UPI/QR. */
  handleCreateDirectBooking?:(data:any,cd:any)=>void
  /** The venue's `paymentSettings`, which decide how much the link collects. */
  paymentSettings?: VenuePaymentSettings | null
}

const CreateBookingModal = ({ open, onClose, selectedSlots,selectedDate,handleCreateBooking,handleCreateDirectBooking,paymentSettings }: CreateBookingModalProps) => {
  const dispatch= useAppDispatch()
  const paymentState= useAppSelector(s=>s.payment)

  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
  }>({});


  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [discount, setDiscount] = useState("");
  // Vendor's own UPI/receipt reference, for direct bookings only.
  const [paymentReference, setPaymentReference] = useState("");
  // Which way the money is collected. Direct is the default because it is what
  // a vendor standing at the counter is usually doing; the payment link is
  // flagged as recommended because the advance is held by the platform rather
  // than depending on the customer turning up. Without a direct handler that
  // option is not rendered at all, so it cannot be the default either.
  const defaultMode: BookingMode = handleCreateDirectBooking ? "DIRECT" : "PAYMENT_LINK";
  const [mode, setMode] = useState<BookingMode>(defaultMode);
  const [sendSms, setSendSms] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);

  // The lookup result is kept here, keyed to the number it was fetched for,
  // rather than read off the redux response. The redux payload outlives the
  // number that produced it: edit the mobile to something incomplete and no new
  // request goes out, so the previous customer's email stayed in the field —
  // locked, because the readOnly flag read that same stale payload.
  const [lookedUpPhone, setLookedUpPhone] = useState("");
  const [emailLocked, setEmailLocked] = useState(false);
  /** Exactly what the lookup filled in — empty unless a customer came back. */
  const prefilledRef = useRef({ name: "", email: "" });
  /** The number the newest in-flight lookup was for — older replies are dropped. */
  const requestedPhoneRef = useRef("");

  /**
   * Drops the looked-up customer when the number stops matching.
   *
   * Only what the lookup itself put in the field is cleared, and only while it
   * is still untouched. A vendor booking for an unregistered customer types the
   * email by hand, then notices a wrong digit in the phone — correcting that
   * digit must not wipe the address they just typed, because no lookup ever
   * supplied it. So the guard is the remembered prefill, never a blanket reset.
   */
  const clearPrefill = () => {
    const filled = prefilledRef.current;
    setName((v) => (filled.name && v === filled.name ? "" : v));
    setEmail((v) => (filled.email && v === filled.email ? "" : v));
    prefilledRef.current = { name: "", email: "" };
    setLookedUpPhone("");
    setEmailLocked(false);
  };

  const applyLookup = (phone: string, res: any) => {
    // Replies can land out of order; only the one for the number still being
    // looked up is allowed to touch the form.
    if (requestedPhoneRef.current !== phone) return;

    const found = res?.payload;
    setLookedUpPhone(phone);

    // No such customer: nothing to prefill, and nothing to remember as
    // prefilled either — whatever is in the fields is the vendor's own typing
    // and stays through any later edit to the number.
    if (!found?.exists) {
      prefilledRef.current = { name: "", email: "" };
      setEmailLocked(false);
      return;
    }

    const nextName = found.name || "";
    const nextEmail = found.email || "";
    if (nextName) setName(nextName);
    if (nextEmail) setEmail(nextEmail);
    prefilledRef.current = { name: nextName, email: nextEmail };
    setEmailLocked(Boolean(nextEmail));
  };

  // const totalPrice = selectedSlots.reduce((sum, s) => sum + s.price, 0);
    const totalPrice = selectedSlots.reduce((sum, s) => sum + s?.pricing?.vendorBasePrice, 0);

  const discountAmount = discount ? (totalPrice * Number(discount)) / 100 : 0;
  const finalPrice = totalPrice - discountAmount;

  // How much of that the payment link actually collects. The rest is cash at
  // the counter, so the vendor needs to see both numbers before choosing a
  // button — the two buttons are two different money flows, not two ways of
  // doing the same thing.
  const split = splitBookingPayment(finalPrice, paymentSettings);

  // Both actions need the same minimum — a name and a real mobile. Greying the
  // buttons out says so up front, rather than letting the click through to a
  // toast that explains what the form could have shown all along.
  const canSubmit = Boolean(name.trim()) && isValidMobile(mobile);

  /**
   * Both buttons collect the same details and differ only in who takes the
   * money: PAYMENT_LINK sends the customer a Razorpay link and leaves the
   * booking pending until they pay; DIRECT confirms the slot straight away
   * because the vendor is collecting on their own UPI/QR.
   */
  const handleSubmit = (submitMode: BookingMode) => {
    if (!name.trim() || !mobile.trim()) {
      toast.error("Please fill in customer name and mobile number");
      return;
    }
    if (mobile.trim().length < 10) {
      toast.error("Please enter a valid mobile number");
      return;
    }

    const paylod: any = {
      turfId:selectedSlots[0]?.turfId,
      sportId:selectedSlots[0]?.sportId,
      courtId:selectedSlots[0]?.courtId,
      date:selectedDate,
      slotIds:selectedSlots?.map(ele=>ele._id),
      customerName:name,
      phoneNumber:parseInt(mobile),
      email,
      discountPercentage : parseFloat(discount),
      finalPrice:finalPrice,
      idempotencyKey:uuidv4(),
    }

    const done = (res:any) => {
      if(res){
        setName(""); setEmail(""); setMobile(""); setDiscount(""); setPaymentReference("");
      }
    };

    if (submitMode === "DIRECT") {
      // Optional: the vendor's own UPI reference, so the booking can still be
      // reconciled against their bank statement later.
      if (paymentReference.trim()) paylod.paymentReference = paymentReference.trim();
      handleCreateDirectBooking?.(paylod, done);
      return;
    }

    handleCreateBooking(paylod, done);
  };


  // Reopening the modal starts a clean booking — nothing from the last
  // customer survives into the next one.
  useEffect(() => {
    if (!open) return;
    setName(""); setEmail(""); setMobile(""); setDiscount("");
    prefilledRef.current = { name: "", email: "" };
    requestedPhoneRef.current = "";
    setLookedUpPhone("");
    setEmailLocked(false);
    setMode(defaultMode);
    // `defaultMode` follows a prop that cannot change while the modal is open,
    // and listing it would let a re-render wipe a half-filled form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">Create Booking</DialogTitle>
          <DialogDescription>Fill in customer details and confirm the booking.</DialogDescription>
        </DialogHeader>

        <div className="text-left mb-12 space-y-3 pt-2 px-4 overflow-y-auto max-h-[60vh] sm:max-h-[70vh]">
          {/* Selected slots summary */}
          <div className="rounded-md bg-secondary p-3">
            <p className="text-sm font-medium text-secondary-foreground">
              {selectedSlots?.length} slot{selectedSlots?.length > 1 ? "s" : ""} selected
            </p>
            <div className="mt-1 flex flex-wrap gap-2">
            {/* <span className="rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">

                {getSlotTimeRange(selectedSlots)}
                </span> */}
              {selectedSlots?.map((s) => (
                <span key={s._id} className="rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                  {/* {s.time} */}
                  {getSlotTimeRange([s])}
                </span>
              ))}
            </div>
          </div>

          {/* Customer details */}
          <div className="space-y-3">
          <div className="space-y-1.5">
              <Label htmlFor="mobile" className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Mobile Number *</Label>
              <Input 
              autoComplete="none"
              
              id="mobile" type="tel"
              maxLength={10}
              placeholder="9876543210" value={mobile} onChange={(e) =>{
                const val = e.target.value.replace(/\D/g, "")
                setMobile(val)

                // The moment the number stops being the one that was looked up,
                // the prefilled customer no longer applies — drop it now rather
                // than waiting on a reply that may never come (an incomplete
                // number fires no request, and a failed one clears nothing).
                if (val !== lookedUpPhone) clearPrefill()

                if (isValidMobile(val)) {
                  requestedPhoneRef.current = val
                  dispatch(getCustomerByPhone({
                    pathparams: val,
                    callback: (res) => applyLookup(val, res),
                  }))
                }
              } } 
             />

              {mobile?.length>0 && !isValidMobile(mobile) && 
              <div className="text-xs font-medium text-red-500 flex gap-1 item-center justify-start m-0 p-0">
                <InfoIcon className="w-3 h-3 mt-0.5"/> 
                <span> {"Enter valid mobile number"}</span>
                </div>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="name" className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> Customer Name *</Label>
              <Input autoComplete="none"
             id="name" placeholder="Enter customer name" 
             
             value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email</Label>
              <Input  autoComplete="none"
            id="email" type="email" placeholder="customer@example.com" 
            className={emailLocked?"bg-flup-surface-alt text-black/80 cursor-not-allowed":""}
                readOnly={emailLocked}
            value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} />
              {emailLocked &&
              <p className="text-xs text-muted-foreground">
                Linked to the existing customer on this number.
              </p>}
            </div>
         
          </div>

          {/* Discount */}
          <div className="space-y-1.5">
            <Label htmlFor="discount" className="flex items-center gap-1.5"><Percent className="h-3.5 w-3.5" /> Discount (%)</Label>
            <Input id="discount" type="number"
             min={0} max={100}
             maxLength={100}
             placeholder="0" value={discount} onChange={(e) => {
              if(e.target.value && (parseInt(e.target.value)<=100) || e.target.value===""){
                setDiscount(e.target.value)
              }
              }} />
          </div>

          {/* Price summary */}
          <div className="rounded-md border bg-muted/50 p-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="flex items-center gap-0.5"><IndianRupee className="h-3.5 w-3.5" />{totalPrice}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-sm text-primary">
                <span>Discount ({discount}%)</span>
                <span>- ₹{discountAmount.toFixed(0)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-base border-t pt-1">
              <span>Total</span>
              <span className="flex items-center gap-0.5"><IndianRupee className="h-4 w-4" />{finalPrice.toFixed(0)}</span>
            </div>
          </div>

          {/* How the money is collected.

              These are two different money flows, not two ways of doing the
              same thing, so they are a choice made up front rather than two
              buttons at the bottom that look interchangeable. Each option
              states its own amounts; the selected one expands into what
              actually happens to the money afterwards. */}
          <div className="space-y-2">
            <p className="text-sm font-medium">How this booking gets paid</p>

            <RadioGroup
              value={mode}
              onValueChange={(v) => setMode(v as BookingMode)}
              className="gap-2"
            >
              {/* Vendor collects the money themselves — slot is booked
                  straight away, no payment link, nothing to settle back. */}
              {handleCreateDirectBooking ? (
                <label
                  htmlFor="mode-direct"
                  className={cn(
                    "block rounded-md border p-3 cursor-pointer transition-colors",
                    mode === "DIRECT" ? "border-primary bg-primary/5" : "hover:bg-muted/50",
                  )}
                >
                  <div className="flex items-start gap-2.5">
                    <RadioGroupItem value="DIRECT" id="mode-direct" className="mt-0.5" />
                    <div className="flex-1 space-y-1.5">
                      <span className="text-sm font-medium flex items-center gap-1.5">
                        <Wallet className="h-3.5 w-3.5 text-muted-foreground" /> Direct booking
                      </span>

                      <p className="text-sm text-muted-foreground">
                        Customer pays you the full{" "}
                        <span className="font-semibold text-foreground">
                          ₹{finalPrice.toFixed(0)}
                        </span>{" "}
                        at the venue.
                      </p>

                      {mode === "DIRECT" ? (
                        <p className="text-xs text-muted-foreground border-t pt-1.5">
                          Nothing is collected online, so there is no settlement — you
                          already hold every rupee, and no platform fee is charged. A
                          cancellation is between you and your customer.
                        </p>
                      ) : null}
                    </div>
                  </div>
                </label>
              ) : null}

              <label
                htmlFor="mode-link"
                className={cn(
                  "block rounded-md border p-3 cursor-pointer transition-colors",
                  mode === "PAYMENT_LINK" ? "border-primary bg-primary/5" : "hover:bg-muted/50",
                )}
              >
                <div className="flex items-start gap-2.5">
                  <RadioGroupItem value="PAYMENT_LINK" id="mode-link" className="mt-0.5" />
                  <div className="flex-1 space-y-1.5">
                    <span className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
                      <Link2 className="h-3.5 w-3.5 text-primary" /> Send payment link
                      <span className="rounded-full bg-primary/15 text-primary px-2 py-0.5 text-[11px] font-semibold">
                        Recommended
                      </span>
                    </span>

                    {mode === "PAYMENT_LINK" ? (
                      <>
                        <div className="space-y-1 pt-0.5">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              {split.partial
                                ? `Paid online now (${split.percent}%)`
                                : "Paid online now"}
                            </span>
                            <span className="font-semibold flex items-center gap-0.5">
                              <IndianRupee className="h-3.5 w-3.5" />{split.onlineNow}
                            </span>
                          </div>

                          {split.partial ? (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Balance at your venue</span>
                              <span className="font-semibold flex items-center gap-0.5">
                                <IndianRupee className="h-3.5 w-3.5" />{split.atVenue}
                              </span>
                            </div>
                          ) : null}
                        </div>

                        <div className="border-t pt-1.5 space-y-1">
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">Payout:</span> the{" "}
                            ₹{split.onlineNow} is collected on BookYourZone&apos;s Razorpay
                            link and settled to you in full — 100%, nothing deducted —
                            after the slot time is over.
                            {split.partial ? " The balance you collect at the venue yourself." : ""}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">If they cancel:</span>{" "}
                            you keep {VENDOR_CANCELLATION_SHARE_PERCENT}% of the amount paid
                            online — ₹{cancellationShare(split.onlineNow)} of the{" "}
                            ₹{split.onlineNow}. On a direct booking a cancellation leaves you
                            with nothing to keep.
                          </p>
                          <p className="text-xs text-muted-foreground">
                            The platform fee and its GST are added to the customer&apos;s
                            link on top of this.
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">
                          {split.partial
                            ? `Customer pays ₹${split.onlineNow} (${split.percent}%) online now, ₹${split.atVenue} at the venue.`
                            : `Customer pays the full ₹${split.onlineNow} online now.`}
                        </p>
                        {/* The reason to pick this one, visible without picking
                            it — Direct is the default, so this card is collapsed
                            when the vendor first reads the screen. */}
                        <p className="text-xs text-primary font-medium">
                          Keep ₹{cancellationShare(split.onlineNow)} even if they cancel.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Direct-booking reference — the money never passes through the
              platform on that route, so this is the only record of it. Hidden
              on the payment-link route, where Razorpay is the record. */}
          {/* {handleCreateDirectBooking && mode === "DIRECT" ? (
            <div className="space-y-2">
              <Label htmlFor="paymentReference" className="flex items-center gap-1.5 text-sm font-medium">
                <IndianRupee className="h-3.5 w-3.5" /> Payment reference
                <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="paymentReference"
                placeholder="UPI ref / receipt no."
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Keeps a trace of the payment you collected — your UPI reference or
                receipt number.
              </p>
            </div>
          ) : null} */}

          {/* Communication */}
          <div className="space-y-2">
            <p className="text-sm font-medium flex items-center gap-1.5"><Send className="h-3.5 w-3.5" /> Send Communication</p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={sendSms} onCheckedChange={(v) => setSendSms(!!v)} /> SMS
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={sendEmail} onCheckedChange={(v) => setSendEmail(!!v)} /> Email
              </label>
            </div>
          </div>
        </div>

        {/* One action, because the choice was already made above. Two buttons
            here would ask the same question twice and let them disagree. */}
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>

          <ButtonWithLoader
            onClick={() => handleSubmit(mode)}
            isLoading={
              mode === "DIRECT"
                ? paymentState?.createDirectBookingResponse?.isLoading
                : paymentState?.createManualBookingResponse?.isLoading
            }
            disabled={!canSubmit}
          >
            {mode === "DIRECT"
              ? `Confirm booking · ₹${finalPrice.toFixed(0)} at venue`
              : `Send payment link · ₹${split.onlineNow} now`}
          </ButtonWithLoader>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateBookingModal;
